import React from 'react';
import PropTypes from 'prop-types';
import { PSPFAQ } from 'components/psp/PSPFAQ';

export const FeaturesAmenitiesAccordion = (props) => {
    // Use featuresDataByCategory if available, otherwise fall back to empty structure
    const featuresDataByCategory = props.facility?.featuresDataByCategory || {};

    // Helper function to check if a category has attributes using featuresDataByCategory structure
    const categoryHasAttributes = (categoryKey) => {
        return featuresDataByCategory[categoryKey]?.hasAttributes === true;
    };

    // Helper function to create feature content sections
    const createFeatureContent = (categories) => {
        return categories
            .filter(({ key }) => categoryHasAttributes(key))
            .map(({ key, title }) => ({
                title,
                content: featuresDataByCategory[key]?.items || []
            }));
    };

    // Helper function to safely decode and format description for markdown rendering
    const getFormattedDescription = (description) => {
        if (!description) return 'Facility description not available.';
        
        const formatText = (text) => {
            return text
                .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '\n\n') // Convert double br tags to double newlines
                .replace(/<br\s*\/?>/gi, '\n\n') // Convert single br tags to double newlines
                .replace(/\n+/g, '\n\n') // Normalize multiple newlines to double newlines
                .trim(); // Remove leading/trailing whitespace
        };

        try {
            return formatText(decodeURIComponent(description));
        } catch (error) {
            // If decodeURIComponent fails, use the original description
            return formatText(description);
        }
    };

    // Facility Description Accordian Content
    const feature0 = {
        id: 'Features & Amenities Accordian',
        noHeader: true,
        content: [{
            question: '### Facility Description',
            answer: `#### About ${props.facility?.name}\n\n${getFormattedDescription(props.facility?.description)}`
        }],
    };

    // Features & Amenities Accordian Content
    const feature1 = {
        id: 'Features & Amenities Accordian',
        noHeader: true,
        content: [{
            question: '### Office & Access Hours',
            answer: {
                accessHours: props.facility?.hours?.access?.text,
                officeHours: props.facility?.hours?.office?.text
            },
        }],
    };

    // Create feature2 only if either access or security has true attributes
    const feature2Content = createFeatureContent([
        { key: 'access', title: 'Access' },
        { key: 'security', title: 'Security' }
    ]);
    const feature2 = feature2Content.length > 0 ? {
        id: 'Features & Amenities Accordian',
        noHeader: true,
        content: [{
            question: '### Accessibility & Security',
            answer: feature2Content,
        }],
    } : null;

    // Create feature3 only if either vehicle requirements or property coverage has true attributes
    const feature3Content = createFeatureContent([
        { key: 'vehicleStorageRequirements', title: 'Vehicle Storage Requirements' },
        { key: 'propertyCoverage', title: 'Property Coverage' }
    ]);
    const feature3 = feature3Content.length > 0 ? {
        id: 'Features & Amenities Accordian',
        noHeader: true,
        content: [{
            question: '### Vehicle Storage & Property Coverage',
            answer: feature3Content,
        }],
    } : null;

    // Create feature4 only if any of payments, billing, or miscellaneous has true attributes
    const feature4Content = createFeatureContent([
        { key: 'payments', title: 'Payments' },
        { key: 'billing', title: 'Billing' },
        { key: 'miscellaneous', title: 'Miscellaneous' }
    ]);
    const feature4 = feature4Content.length > 0 ? {
        id: 'Features & Amenities Accordian',
        noHeader: true,
        content: [{
            question: '### Payments, Billing and Miscellaneous',
            answer: feature4Content,
        }],
    } : null;

    // Create feature5 only if premium services has any true attributes
    const feature5 = categoryHasAttributes('premiumServices') ? {
        id: 'Features & Amenities Accordian',
        noHeader: true,
        content: [{
            question: '### Premium Services',
            answer: [{
                content: featuresDataByCategory.premiumServices?.items || []
            }],
        }],
    } : null;

    // Safety check - don't render anything if no facility data
    if (!props.facility) {
        return null;
    }

    return (
        <>
            {props.facility.description && <PSPFAQ {...feature0} faqSchema={false} />}
            <PSPFAQ {...feature1} type="access&hours" faqSchema={false} />
            {feature2 && <PSPFAQ {...feature2} type="facility" faqSchema={false} />}
            {feature3 && <PSPFAQ {...feature3} type="facility" faqSchema={false} />}
            {feature4 && <PSPFAQ {...feature4} type="facility" faqSchema={false} />}
            {feature5 && <PSPFAQ {...feature5} type="facility" faqSchema={false} />}
        </>
    );
};

FeaturesAmenitiesAccordion.propTypes = {
    facility: PropTypes.shape({
        featuresDataByCategory: PropTypes.object,
        groupedAmenities: PropTypes.object,
        name: PropTypes.string,
        description: PropTypes.string,
        hours: PropTypes.shape({
            access: PropTypes.shape({
                text: PropTypes.string
            }),
            office: PropTypes.shape({
                text: PropTypes.string
            })
        })
    }).isRequired
};

export default FeaturesAmenitiesAccordion;